import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, CalendarOff } from 'lucide-react';
import { DataTable, StatusPill, Button, PageHeader, Modal, Loader } from '../../components';
import leaveService, { canApproveLeave } from '../../services/leaveService';
import { useAuth } from '../../context/AuthContext';
import ApplyLeaveModal from './ApplyLeaveModal';
import { quantityLabel } from './leaveUnits';

/**
 * My leave — reached from the profile dropdown, available to every role.
 *
 * Deliberately scoped to the signed-in user: the API endpoint behind it takes
 * no employee id at all, so this page can be open to everyone without leaking
 * a colleague's sickness record. Managers see the organisation's requests on
 * the separate Leave & Approvals screen.
 */

const STATUS_TONE = {
  pending: 'warn',
  approved: 'good',
  rejected: 'crit',
  cancelled: 'mute',
};

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  // Not a status — swaps the request list for "how much have I taken, and
  // when", which is the other half of what people come to this page for.
  { key: 'summary', label: 'My summary' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CARD = 'bg-white dark:bg-[#262b33] border border-[#E2E8F0] dark:border-gray-800 rounded-2xl shadow-sm';

/**
 * How each request shape reads in a list. 'hours_total' is worth naming
 * distinctly from 'hours': one is part of a single shift, the other is a block
 * of hours that may span several, and they cost different amounts of allowance.
 * Rows written before duration_type existed fall through to full day(s).
 */
const DURATION_LABELS = {
  hours: 'Part of a shift',
  hours_total: 'Hours across shifts',
  full_day: 'Full day(s)',
};

const MyLeave = () => {
  const { allAvailableMenus, menuIds } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0, approved_hours: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [applyOpen, setApplyOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // My annual leave allowance for the current year. Held here rather than in
  // the apply modal because it is also the page's headline figure, and because
  // one fetch feeding both means the banner and the form can never quote
  // different numbers.
  const [balance, setBalance] = useState(null);

  // My summary tab
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsYear, setStatsYear] = useState(new Date().getFullYear());

  const isSummary = tab === 'summary';

  const fetchLeave = useCallback(async () => {
    // The summary tab loads its own data; don't refetch the request list for it.
    if (isSummary) return;
    try {
      setLoading(true);
      const res = await leaveService.getMyLeave(tab);
      if (res?.success) {
        setRows(res.data || []);
        setSummary(res.summary || {});
      } else {
        toast.error(res?.message || 'Could not load your leave');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load your leave');
    } finally {
      setLoading(false);
    }
  }, [tab, isSummary]);

  useEffect(() => { fetchLeave(); }, [fetchLeave]);

  /**
   * The allowance banner. Always the CURRENT year — the balance that decides
   * whether a request can be made, regardless of which year the summary tab
   * happens to be looking at.
   *
   * A failure here is swallowed on purpose: not knowing the balance must not
   * stop the page rendering the requests, which is what most visits are for.
   * The banner simply doesn't appear, and the API still enforces the allowance.
   */
  const fetchBalance = useCallback(async () => {
    try {
      const res = await leaveService.getMyBalance(new Date().getFullYear());
      if (res?.success) setBalance(res.data);
    } catch (_) {
      // Left as null — see above.
    }
  }, []);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  const fetchStats = useCallback(async () => {
    if (!isSummary) return;
    try {
      setStatsLoading(true);
      const res = await leaveService.getMyStats(statsYear);
      if (res?.success) setStats(res.data);
      else toast.error(res?.message || 'Could not load your summary');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load your summary');
    } finally {
      setStatsLoading(false);
    }
  }, [isSummary, statsYear]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      setCancelling(true);
      const res = await leaveService.cancelLeave(cancelTarget.id);
      if (res?.success) {
        toast.success('Leave request cancelled');
        setCancelTarget(null);
        fetchLeave();
        // Cancelling releases the days the request was holding, so the banner
        // is now wrong until it is re-read.
        fetchBalance();
      } else {
        toast.error(res?.message || 'Could not cancel the request');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not cancel the request');
    } finally {
      setCancelling(false);
    }
  };

  const columns = [
    {
      key: 'type',
      header: 'Type',
      render: (r) => (
        <div className="min-w-0">
          <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">{r.leave_type_label}</div>
          {r.notes && (
            <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 truncate max-w-[260px]" title={r.notes}>
              {r.notes}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'period',
      header: 'When',
      render: (r) => (
        <div>
          <div className="text-[13px] text-gray-700 dark:text-gray-300">{r.period_label}</div>
          <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">
            {DURATION_LABELS[r.duration_type] || 'Full day(s)'}
          </div>
        </div>
      ),
    },
    {
      key: 'hours',
      header: 'Amount',
      render: (r) => (
        <div>
          <div className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">
            {r.leave_days != null
              ? quantityLabel(r.leave_days, r.day_hours, balance?.unit || 'days')
              : (r.hours != null ? `${r.hours}h` : '—')}
          </div>
          {/* Only annual leave is charged, so saying so on the rows that aren't
              is what stops the amount column reading as a bill. */}
          {r.leave_days != null && r.leave_type !== 'annual' && (
            <div className="text-[11.5px] text-[#94A3B8] dark:text-gray-500">
              not off allowance
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div className="flex flex-col items-start gap-1">
          <StatusPill tone={STATUS_TONE[r.status] || 'mute'}>{r.status_label}</StatusPill>
          {r.review_note && (
            <span className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 max-w-[220px]" title={r.review_note}>
              “{r.review_note}”
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        // Only a request nobody has decided on can be withdrawn — once it's
        // approved it's on the rota and has to go back through a manager.
        r.status === 'pending' ? (
          <Button variant="ghost" size="sm" onClick={() => setCancelTarget(r)}>Cancel</Button>
        ) : null
      ),
    },
  ];

  const tile = (label, value, tone) => (
    <div className={`${CARD} p-4`}>
      <div className={`text-[22px] font-extrabold ${tone}`}>{value ?? 0}</div>
      <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">{label}</div>
    </div>
  );

  const hrs = (n) => `${Number(n || 0)}h`;

  /**
   * My year: the type split, a month-by-month strip, and every leave listed
   * under the month it falls in — "how much" and "when", in one place.
   */
  const renderSummary = () => {
    if (statsLoading) return <Loader text="Loading your summary…" fullHeight />;

    const totals = stats?.totals;
    const months = stats?.months || [];
    const leaves = stats?.leaves || [];
    const peak = Math.max(...months.map((m) => m.total_hours || 0), 1);
    const firstYear = stats?.first_leave_year ?? new Date().getFullYear();
    const years = [];
    for (let y = new Date().getFullYear(); y >= Math.min(firstYear, new Date().getFullYear()); y -= 1) {
      years.push(y);
    }

    // Group the year's leave by month so the list reads like a diary rather
    // than an undifferentiated run of rows.
    const byMonth = leaves.reduce((acc, l) => {
      const m = Number(String(l.start_date).substring(5, 7));
      (acc[m] = acc[m] || []).push(l);
      return acc;
    }, {});

    return (
      <>
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-[#E2E8F0] dark:border-gray-800">
          <p className="flex-1 min-w-[200px] text-[13px] text-[#64748B] dark:text-gray-400">
            Approved leave you took in {stats?.year ?? statsYear}.
          </p>
          <div className="flex items-center gap-1">
            {years.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => setStatsYear(y)}
                className={`px-3 py-1.5 rounded-lg text-[13px] font-semibold transition ${Number(statsYear) === y
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f]'
                  }`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>

        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E2E8F0] dark:bg-gray-800 border-b border-[#E2E8F0] dark:border-gray-800">
            {[
              ['Total hours', hrs(totals.total_hours)],
              ['Full days', totals.total_days],
              ['Sick', hrs(totals.sick_hours)],
              ['Annual Leave', hrs(totals.annual_hours)],
              ['Other', hrs(totals.other_hours)],
            ].map(([label, value]) => (
              <div key={label} className="bg-white dark:bg-[#262b33] p-3.5">
                <div className="text-[17px] font-extrabold text-[#0B1220] dark:text-gray-100">{value}</div>
                <div className="text-[11.5px] text-[#64748B] dark:text-gray-400 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="p-4 border-b border-[#E2E8F0] dark:border-gray-800">
          <p className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-3">
            Your leave hours by month
          </p>
          <div className="flex items-end gap-1.5 h-[80px]">
            {months.map((m) => {
              const height = Math.round(((m.total_hours || 0) / peak) * 100);
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full gap-1.5">
                  <div className="w-full flex-1 flex items-end" title={`${MONTH_NAMES[m.month - 1]}: ${hrs(m.total_hours)}`}>
                    <div
                      style={{ height: `${Math.max(height, 2)}%` }}
                      className={`w-full rounded-t ${m.total_hours > 0 ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-800'}`}
                    />
                  </div>
                  <span className="text-[10px] font-semibold text-[#94A3B8] dark:text-gray-500">
                    {MONTH_NAMES[m.month - 1].substring(0, 3)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {leaves.length === 0 ? (
          <div className="p-10 text-center">
            <CalendarOff className="w-10 h-10 mx-auto text-[#CBD5E1] dark:text-gray-700" />
            <p className="text-[14px] font-semibold text-[#0B1220] dark:text-gray-100 mt-3">
              No approved leave in {stats?.year ?? statsYear}
            </p>
            <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">
              Leave shows here once your manager approves it.
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-5">
            {Object.keys(byMonth).map(Number).sort((a, b) => a - b).map((m) => (
              <div key={m}>
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">
                    {MONTH_NAMES[m - 1]}
                  </h3>
                  <span className="text-[12px] text-[#64748B] dark:text-gray-400">
                    {hrs(months[m - 1]?.total_hours)}
                  </span>
                </div>
                <div className="space-y-2">
                  {byMonth[m].map((l) => (
                    <div
                      key={l.id}
                      className="flex items-start gap-3 rounded-xl border border-[#E2E8F0] dark:border-gray-800 p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">
                          {l.leave_type_label}
                        </div>
                        <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5">
                          {l.period_label}
                          {` · ${(DURATION_LABELS[l.duration_type] || 'Full day(s)').toLowerCase()}`}
                        </div>
                        {l.notes && (
                          <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1 italic">“{l.notes}”</p>
                        )}
                      </div>
                      <span className="text-[13px] font-bold text-gray-700 dark:text-gray-200 whitespace-nowrap">
                        {l.hours != null ? `${l.hours}h` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    // `space-y-6` is what separates the header from the cards below. PageHeader
    // bottom-aligns its action button, so without it the "Apply for leave"
    // button sits flush against the top of the stat tiles. `pb-8` is bottom
    // breathing room — same wrapper every other page uses.
    <div className="w-full space-y-6 pb-8">
      <PageHeader
        title="My leave"
        subtitle="Request time off and track its approval"
        actions={(
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setApplyOpen(true)}>
            Apply for leave
          </Button>
        )}
      />

      {/* The allowance, ahead of the request counts. "How many days have I got
          left" is the question people open this page with; how many requests are
          pending is the detail. */}
      {balance && (
        <div className={`${CARD} p-4`}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[11.5px] font-semibold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">
                Annual leave left · {balance.year}
              </p>
              <p className={`text-[30px] font-extrabold leading-tight ${balance.remaining_days <= 0
                ? 'text-rose-500'
                : 'text-[#0B1220] dark:text-gray-100'}`}
              >
                {quantityLabel(balance.remaining_days, balance.day_hours, balance.unit)}
              </p>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {[
                ['Allowance', balance.entitlement_days],
                ['Taken', balance.used_days],
                ['Awaiting approval', balance.pending_days],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-[15px] font-bold text-gray-700 dark:text-gray-200">
                    {quantityLabel(value, balance.day_hours, balance.unit)}
                  </div>
                  <div className="text-[11.5px] text-[#64748B] dark:text-gray-400">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* The conversion rate, stated once. Everything on this page is
              measured in it, and a part-timer seeing "3 days" for a week off
              needs to know why. */}
          <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-3 pt-3 border-t border-[#F1F5F9] dark:border-gray-800">
            {balance.has_roster ? (
              <>
                A day of leave is one of your shifts —{' '}
                <span className="font-semibold">{balance.day_hours} hours</span>
                {balance.shift_label ? ` (${balance.shift_label})` : ''}.
              </>
            ) : (
              <>
                No shift pattern is on file for you, so a day of leave is counted as{' '}
                <span className="font-semibold">8 hours</span>. Ask your manager to set your
                rota pattern for an accurate balance.
              </>
            )}
            {balance.entitlement_notes ? ` ${balance.entitlement_notes}` : ''}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tile('Pending', summary.pending, 'text-amber-500')}
        {tile('Approved', summary.approved, 'text-emerald-600')}
        {tile('Rejected', summary.rejected, 'text-rose-500')}
        {tile('Approved hours', summary.approved_hours, 'text-blue-600')}
      </div>

      <div className={`${CARD} overflow-hidden`}>
        <div className="flex items-center gap-1 p-3 border-b border-[#E2E8F0] dark:border-gray-800 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition ${tab === t.key
                ? 'bg-blue-600 text-white'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f]'
                }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {isSummary ? renderSummary() : (
          <DataTable
            bare
            columns={columns}
            data={rows}
            loading={loading}
            rowKey={(r) => r.id}
            emptyIcon={CalendarOff}
            emptyTitle="No leave requests"
            emptyText={tab === 'all'
              ? 'When you request time off it will appear here with its approval status.'
              : `You have no ${tab} leave requests.`}
          />
        )}
      </div>

      {canApproveLeave(allAvailableMenus, menuIds) && (
        <p className="text-[12.5px] text-[#94A3B8] dark:text-gray-500">
          Looking for your team's requests? They're under <span className="font-semibold">Staff &rarr; Leave &amp; Approval</span> in the sidebar.
        </p>
      )}

      <ApplyLeaveModal
        isOpen={applyOpen}
        onClose={() => setApplyOpen(false)}
        balance={balance}
        // The API returns the balance after the request is held, so take it
        // rather than re-fetching — otherwise the banner shows the pre-request
        // figure for as long as the round trip takes.
        onSubmitted={(fresh) => {
          if (fresh) setBalance(fresh); else fetchBalance();
          fetchLeave();
        }}
      />

      <Modal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        title="Cancel leave request"
        maxWidth="max-w-[420px]"
      >
        <p className="text-[14px] text-[#64748B] dark:text-gray-400">
          Withdraw your {cancelTarget?.leave_type_label?.toLowerCase()} request for{' '}
          <span className="font-semibold text-gray-700 dark:text-gray-200">{cancelTarget?.period_label}</span>?
          You can apply again at any time.
        </p>
        <div className="flex items-center justify-end gap-2 pt-5">
          <Button variant="secondary" onClick={() => setCancelTarget(null)}>Keep it</Button>
          <Button variant="danger" loading={cancelling} onClick={confirmCancel}>
            {cancelling ? 'Cancelling…' : 'Cancel request'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default MyLeave;
